<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Respuestas para reembolsos</h1>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined dismissible class="mb-3">{{ error }}</v-alert>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <!-- Crear nueva respuesta -->
          <v-card-text v-if="can('refund-reply-templates:create')" class="pb-0">
            <v-row class="ma-0" align="start">
              <v-select
                v-model="newCategory"
                :items="categoryOptions"
                item-text="text"
                item-value="value"
                label="Categoría"
                dense outlined hide-details
                class="mr-2"
                style="max-width: 180px"
              />
              <v-text-field
                v-model="newTitle"
                label="Título"
                dense outlined hide-details
                class="mr-2"
                style="max-width: 220px"
                placeholder="Título de la respuesta..."
              />
              <v-textarea
                v-model="newContent"
                label="Contenido"
                dense outlined hide-details
                class="mr-2"
                rows="2"
                auto-grow
                style="max-width: 500px"
                placeholder="Texto de la respuesta tipo..."
              />
              <v-btn
                color="primary"
                :loading="creating"
                :disabled="!newCategory || !newTitle.trim() || !newContent.trim()"
                @click="createItem"
                class="mt-1"
              >
                <v-icon left>mdi-plus</v-icon>Agregar
              </v-btn>
            </v-row>
          </v-card-text>

          <!-- Tabla -->
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading"
            hide-default-footer
            :items-per-page="-1"
            class="elevation-0 mt-2"
            no-data-text="No hay respuestas registradas"
          >
            <template v-slot:item.category="{ item }">
              <v-select
                v-if="editing === item.id"
                v-model="editData.category"
                :items="categoryOptions"
                item-text="text"
                item-value="value"
                dense outlined hide-details
                style="max-width: 180px"
              />
              <v-chip v-else small :color="categoryColor(item.category)" dark>
                {{ categoryLabel(item.category) }}
              </v-chip>
            </template>

            <template v-slot:item.title="{ item }">
              <v-text-field
                v-if="editing === item.id"
                v-model="editData.title"
                dense outlined hide-details
                style="max-width: 220px"
              />
              <span v-else class="font-weight-medium">{{ item.title }}</span>
            </template>

            <template v-slot:item.content="{ item }">
              <v-textarea
                v-if="editing === item.id"
                v-model="editData.content"
                dense outlined hide-details
                rows="2"
                auto-grow
                style="max-width: 500px"
              />
              <span v-else class="grey--text text--darken-2" style="white-space: pre-line;">
                {{ item.content.length > 120 ? item.content.slice(0, 120) + '...' : item.content }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <div v-if="editing === item.id" class="d-flex">
                <v-btn small color="primary" :loading="saving" @click="saveItem(item)">
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn small text class="ml-1" @click="editing = null">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else class="d-flex">
                <v-btn v-if="can('refund-reply-templates:update')" icon small @click="startEdit(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-if="can('refund-reply-templates:delete')"
                  icon
                  small
                  class="ml-1"
                  color="error"
                  @click="confirmRemove(item)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Confirm delete dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Eliminar respuesta</v-card-title>
        <v-card-text>¿Estás seguro de que deseas eliminar <strong>{{ itemToDelete && itemToDelete.title }}</strong>?</v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="deleting" @click="removeItem">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RespuestasTipoService from '../services/respuestasTipo.service';
import { can } from '../utils/permissions';

const CATEGORY_OPTIONS = [
  { value: 'corregir', text: 'Corregir' },
  { value: 'rechazar', text: 'Rechazar' },
  { value: 'aprobar',  text: 'Aprobar'  },
];

export default {
  name: 'RespuestasTipo',
  data() {
    return {
      loading: false,
      creating: false,
      saving: false,
      deleting: false,
      error: null,
      items: [],
      newCategory: 'corregir',
      newTitle: '',
      newContent: '',
      editing: null,
      editData: { category: '', title: '', content: '' },
      deleteDialog: false,
      itemToDelete: null,
      categoryOptions: CATEGORY_OPTIONS,
      headers: [
        { text: 'Categoría', value: 'category', sortable: true,  width: '160px' },
        { text: 'Título',    value: 'title',    sortable: true,  width: '220px' },
        { text: 'Contenido', value: 'content',  sortable: false },
        { text: '',          value: 'actions',  sortable: false, align: 'end', width: '100px' },
      ],
    };
  },
  mounted() {
    this.loadItems();
  },
  methods: {
    can,
    categoryLabel(category) {
      const opt = CATEGORY_OPTIONS.find(o => o.value === category);
      return opt ? opt.text : category;
    },
    categoryColor(category) {
      const map = { corregir: 'orange darken-1', rechazar: 'red darken-1', aprobar: 'green darken-1' };
      return map[category] || 'grey';
    },

    async loadItems() {
      this.loading = true;
      const res = await RespuestasTipoService.getAll();
      if (res.status === 'ok') this.items = res.data;
      this.loading = false;
    },

    async createItem() {
      if (!this.newCategory || !this.newTitle.trim() || !this.newContent.trim()) return;
      this.creating = true;
      this.error = null;
      const res = await RespuestasTipoService.create({
        category: this.newCategory,
        title: this.newTitle.trim(),
        content: this.newContent.trim(),
      });
      if (res.status === 'ok') {
        this.newTitle = '';
        this.newContent = '';
        this.newCategory = 'corregir';
        await this.loadItems();
      } else {
        this.error = 'No se pudo crear la respuesta.';
      }
      this.creating = false;
    },

    startEdit(item) {
      this.editing = item.id;
      this.editData = { category: item.category, title: item.title, content: item.content };
      this.error = null;
    },

    async saveItem(item) {
      if (!this.editData.title.trim() || !this.editData.content.trim()) return;
      this.saving = true;
      this.error = null;
      const res = await RespuestasTipoService.update(item.id, {
        category: this.editData.category,
        title: this.editData.title.trim(),
        content: this.editData.content.trim(),
      });
      if (res.status === 'ok') {
        item.category = this.editData.category;
        item.title = this.editData.title.trim();
        item.content = this.editData.content.trim();
        this.editing = null;
      } else {
        this.error = 'No se pudo actualizar la respuesta.';
      }
      this.saving = false;
    },

    confirmRemove(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async removeItem() {
      this.deleting = true;
      const res = await RespuestasTipoService.remove(this.itemToDelete.id);
      if (res.status === 'ok') {
        this.items = this.items.filter(i => i.id !== this.itemToDelete.id);
        this.deleteDialog = false;
        this.itemToDelete = null;
      } else {
        this.error = 'No se pudo eliminar la respuesta.';
      }
      this.deleting = false;
    },
  },
};
</script>
